/**
 * RaceCatalog — first visible Vibe view (Sprint U2.3).
 *
 * Lists every race seeded in the Odyssey universe. Clicking a race opens
 * its detail panel on the right. Tier-aware rendering happens server-side
 * (owner vs guest) — this component just renders whatever the API returns.
 */

import { useEffect, useMemo, useState } from "react";

import { fetchRaces, type RaceDTO } from "../api.js";

type LoadState =
  | { kind: "loading" }
  | { kind: "error"; message: string }
  | { kind: "ready"; races: RaceDTO[] };

export function RaceCatalog() {
  const [state, setState] = useState<LoadState>({ kind: "loading" });
  const [selectedId, setSelectedId] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchRaces()
      .then((races) => {
        if (cancelled) return;
        setState({ kind: "ready", races });
        if (races.length > 0) setSelectedId(races[0].id);
      })
      .catch((err: Error) => {
        if (cancelled) return;
        setState({ kind: "error", message: err.message });
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedRace = useMemo(() => {
    if (state.kind !== "ready") return null;
    return state.races.find((r) => r.id === selectedId) ?? null;
  }, [state, selectedId]);

  if (state.kind === "loading") {
    return <div className="placeholder">Loading races…</div>;
  }
  if (state.kind === "error") {
    return (
      <div className="placeholder" style={{ color: "#ef4444" }}>
        <strong>Failed to load races:</strong> {state.message}
      </div>
    );
  }

  if (state.races.length === 0) {
    return (
      <div className="placeholder">
        No races in this universe yet. The seed may not have run against this database.
      </div>
    );
  }

  return (
    <div className="race-catalog">
      <aside className="race-list">
        <div className="race-list-header">
          <h2>Races</h2>
          <span className="count">{state.races.length}</span>
        </div>
        <ul>
          {state.races.map((race) => (
            <li key={race.id}>
              <button
                className={`race-list-item ${race.id === selectedId ? "active" : ""}`}
                onClick={() => setSelectedId(race.id)}
              >
                <span className="name">{race.name}</span>
                {race.isImmortal ? (
                  <span className="badge immortal">immortal</span>
                ) : race.lifespanMin != null && race.lifespanMax != null ? (
                  <span className="lifespan">
                    {race.lifespanMin}–{race.lifespanMax} yrs
                  </span>
                ) : (
                  <span className="lifespan">Unknown</span>
                )}
              </button>
            </li>
          ))}
        </ul>
      </aside>
      <section className="race-detail">
        {selectedRace ? <RaceDetail race={selectedRace} /> : <div className="placeholder">Pick a race.</div>}
      </section>
    </div>
  );
}

function RaceDetail({ race }: { race: RaceDTO }) {
  return (
    <article>
      <header className="detail-header">
        <h2>{race.name}</h2>
        {race.category && <span className="category">{race.category}</span>}
      </header>

      <dl className="detail-grid">
        <dt>Lifespan</dt>
        <dd>
          {race.isImmortal
            ? "Immortal"
            : race.lifespanMin != null && race.lifespanMax != null
              ? `${race.lifespanMin}–${race.lifespanMax} years`
              : "Unknown"}
        </dd>

        <dt>Subtypes</dt>
        <dd>
          {race.subtypes.length > 0 ? (
            <ul className="inline-list">
              {race.subtypes.map((s, i) => {
                const subtype = s as { name?: string };
                return <li key={i}>{subtype.name ?? String(s)}</li>;
              })}
            </ul>
          ) : (
            <span className="empty">—</span>
          )}
        </dd>

        <dt>Homeworlds</dt>
        <dd>
          {race.homeworldIds.length > 0 ? (
            <span className="muted">{race.homeworldIds.length} linked</span>
          ) : (
            <span className="empty">—</span>
          )}
        </dd>
      </dl>

      {race.description.length > 0 && (
        <section className="description">
          <h3>Description</h3>
          {race.description.map((entry, i) => (
            <p key={i} className={`tiered tier-${entry.tier}`}>
              {entry.tier === "tier_2" && <span className="tier-badge">tier 2</span>}
              {entry.text}
            </p>
          ))}
        </section>
      )}

      {race.traits.length > 0 && (
        <section className="traits">
          <h3>Traits</h3>
          {race.traits.map((entry, i) => (
            <p key={i} className={`tiered tier-${entry.tier}`}>
              {entry.tier === "tier_2" && <span className="tier-badge">tier 2</span>}
              {entry.text}
            </p>
          ))}
        </section>
      )}
    </article>
  );
}
